import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { Button } from 'react-covideo-common';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import { ListDropdownMenu } from 'lib/components';
import { HiPencil, HiOutlineDuplicate, HiEye } from 'react-icons/hi';
import { IoMdSettings } from 'react-icons/io';
import { MdEditNote, MdGroup } from 'react-icons/md';
import { ICON_COLOR } from 'app/pages/library/boards/main/style';
import { MdDeleteForever } from 'react-icons/md';
import { DeleteLandingPageDesignModal } from '../modals/DeleteLandingPageDesignModal';
import { RenameLandingPageDesignModal } from '../modals/RenameLandingPageDesignModal';
import { LandingPageDesignAccess } from 'lib/api/superadmin/landingPageDesigns/useQueryLandingPageDesigns';
import { useDuplicateLandingPageDesignMutation } from 'lib/api/superadmin/landingPageDesigns/useDuplicateLandingPageDesign';
import { useIsMutating } from 'react-query';
import { LP_BUILDER_MODAL_STEPS } from '../constants';
import { ILandingPageDesignResponse, LP_BUILDER_MODAL_STEP } from '../types';
import AddToGroupModal from '../modals/AddToGroupModal';

const LandingPageDesignActionsWrapper = styled.div`
  position: relative;
`;

export interface ILandingPageDesignActionItems {
  landingPageDesign: ILandingPageDesignResponse;
  accessList: LandingPageDesignAccess[];
  showLandingPagePreview: (landingPageId: number) => Promise<void>;
  handleEditTemplateDesignClick: (type: LP_BUILDER_MODAL_STEP) => void;
}

enum LandingPageDesignModalValue {
  Delete = 'delete',
  Rename = 'rename',
  ManageGroups = 'manage-groups',
}

type LandingPageDesignModalType = LandingPageDesignModalValue;

interface IActionItem {
  title: string;
  titleColor: string;
  icon: JSX.Element;
  onClick?: () => void;
}

export const LandingPageDesignActionItems = ({
  landingPageDesign,
  accessList,
  showLandingPagePreview,
  handleEditTemplateDesignClick,
}: ILandingPageDesignActionItems) => {
  const theme = useTheme();
  const [modal, setModal] = useState<LandingPageDesignModalType | null>(null);
  const [menuOpenedForBoard, setMenuOpenedForBoard] = useState<number | null>(
    null
  );
  const { id: templateId, title } = landingPageDesign;

  const { mutateAsync: duplicate, isLoading: isDuplicating } =
    useDuplicateLandingPageDesignMutation();

  const isMutating = useIsMutating();
  const loading = isDuplicating || isMutating > 0;

  const actionItems: IActionItem[] = [
    {
      title: 'Edit Template',
      titleColor: theme.colors.neutral[100],
      icon: <HiPencil color={ICON_COLOR} size={20} />,
      onClick: () =>
        handleEditTemplateDesignClick(LP_BUILDER_MODAL_STEPS.design),
    },
    {
      title: 'Manage Groups',
      titleColor: theme.colors.neutral[100],
      icon: <MdGroup color={ICON_COLOR} size={20} />,
      onClick: () => setModal(LandingPageDesignModalValue.ManageGroups),
    },
    {
      title: 'Rename Template',
      titleColor: theme.colors.neutral[100],
      icon: <MdEditNote color={ICON_COLOR} size={20} />,
      onClick: () => setModal(LandingPageDesignModalValue.Rename),
    },
    {
      title: 'View Template',
      titleColor: theme.colors.neutral[100],
      icon: <HiEye color={ICON_COLOR} size={20} />,
      onClick: () => showLandingPagePreview(templateId),
    },
    {
      title: 'Duplicate Template',
      titleColor: theme.colors.neutral[100],
      icon: <HiOutlineDuplicate color={ICON_COLOR} size={20} />,
      onClick: () => duplicate(templateId),
    },
    {
      title: 'Manage Access',
      titleColor: theme.colors.neutral[100],
      icon: <IoMdSettings color={ICON_COLOR} size={20} />,
      onClick: () =>
        handleEditTemplateDesignClick(LP_BUILDER_MODAL_STEPS.manageAccess),
    },
    {
      title: 'Delete Template',
      titleColor: theme.colors.danger[100],
      icon: <MdDeleteForever color={theme.colors.danger[100]} size={20} />,
      onClick: () => setModal(LandingPageDesignModalValue.Delete),
    },
  ];

  const closeModalHandler = () => {
    setModal(null);
  };

  return (
    <LandingPageDesignActionsWrapper>
      <Button
        icon={
          <PiDotsThreeOutlineVerticalFill
            onClick={() => setMenuOpenedForBoard(templateId)}
            color={theme.colors.neutral[100]}
            style={{ cursor: 'pointer' }}
            size={18}
          />
        }
        variant='transparent'
        size='small'
        disabled={loading}
      />
      <ListDropdownMenu
        isMenuOpen={menuOpenedForBoard === templateId}
        items={actionItems}
        id={templateId.toString()}
        setIsMenuOpen={() => setMenuOpenedForBoard(null)}
        extendStyles={{
          menuWrapper: {
            width: '260px',
            padding: '20px',
            right: 0,
          },
          menuItem: {
            justifyItems: 'flex-start',
          },
          itemIcon: {
            margin: '0px',
          },
          itemTitle: {
            fontWeight: 500,
            fontSize: 14,
          },
        }}
      />
      {modal === LandingPageDesignModalValue.Delete && (
        <DeleteLandingPageDesignModal
          templateId={templateId}
          closeModalHandler={closeModalHandler}
          accessList={accessList}
        />
      )}
      {modal === LandingPageDesignModalValue.Rename && (
        <RenameLandingPageDesignModal
          templateId={templateId}
          title={title}
          closeModalHandler={closeModalHandler}
        />
      )}

      {modal === LandingPageDesignModalValue.ManageGroups && (
        <AddToGroupModal
          handleCloseModal={closeModalHandler}
          selectedLandingPageDesigns={[landingPageDesign]}
        />
      )}
    </LandingPageDesignActionsWrapper>
  );
};
